import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import { IParkingLotSessionPaymentsPaymentData, ParkingLotSessionPaymentsPaymentOption } from 'components/providers/ApiProvider'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { formatMoney } from 'utils'
import { useParkingLot } from 'components/views/parking-lot/ParkingLotProvider'
import { theme } from 'components/providers/ThemeProvider'
import PaymentOptionChip from 'Elements/PaymentOptionChip'
import MobillyPaymentOptionChip from 'Elements/MobillyPaymentOptionChip'
import IconButton from '@material-ui/core/IconButton'
import IconRefund from '@material-ui/icons/Undo'
import IconDetach from '@material-ui/icons/LinkOff'
import { useMessage } from 'Elements/Message'
import RefundPaymentDialog, { RefundPaymentDialogData } from 'components/views/parking-lot/partials/RefundPaymentDialog'
import { Money } from '@material-ui/icons'
import {
  ListSectionBody,
  ListSectionDatetime,
  ListSectionIdCell,
  ListSectionSkeletonRow,
} from 'components/views/parking-lot/elements/ListSection'

type Props = {
  session: {
    id: number,
    entry_at: string,
    exit_at?: string,
    modify?: boolean,
  }
  expand: boolean
}
type Functions = {}
type AllProps = Props & Functions

const ListSessionPayments: FunctionComponent<AllProps> = ({ session, expand }) => {
  if (!session) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { loadPayments, setLoadDetachPaymentList } = useParkingLot()

  const [error, setError] = useState<boolean>(false)
  const [modify, setModify] = useState<boolean>(false)
  const [payments, setPayments] = useState<IParkingLotSessionPaymentsPaymentData[]>(null)

  const loaded = useRef(false)

  const [openRefundPaymentDialog, setOpenRefundPaymentDialog] = useState(false)
  const [refundPaymentDialogData, setRefundPaymentDialogData] = useState<RefundPaymentDialogData | null>(null)

  const { showSuccess, showError } = useMessage()

  const load = () => {
    api.getParkingLotSessionPayments({ session_id: session.id })
      .then(data => {
        setModify(Boolean(data.modify && (!session.exit_at || session.modify)))
        setPayments(data.payments)
      })
      .catch(() => {
        setError(true)
      })
  }

  useEffect(() => {
    if (!expand || loaded.current) {
      return
    }

    loaded.current = true

    load()
  }, [expand])

  useEffect(() => {
    if (!loadPayments) {
      return
    }

    if (!expand) {
      loaded.current = false
      setPayments(null)

      return
    }

    load()
  }, [loadPayments])

  const handleRefund = (payment: IParkingLotSessionPaymentsPaymentData) => {
    setRefundPaymentDialogData({ session_id: session.id, payment_id: payment.id })
    setOpenRefundPaymentDialog(true)
  }

  const handleDetach = (payment: IParkingLotSessionPaymentsPaymentData) => {
    if (!confirm(t('confirms.detachPayment'))) {
      return
    }

    api.detachParkingLotSessionPayment({
      session_id: session.id,
      payment_id: payment.id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        setLoadDetachPaymentList()

        showSuccess(t('messages.paymentDetached'))
      })
      .catch(() => {
        showError(t('alerts.couldNotDetachPayment'))
      })
  }

  const errorMessage = error ? `${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}` : null

  return (
    <>
      <ListSectionBody expand={expand} error={errorMessage} empty={Boolean(payments && !payments.length)}>
        <TableHead>
          {!payments && <ListSectionSkeletonRow />}
          {payments && (
            <TableRow>
              <TableCell>{t('paidAt')}</TableCell>
              <TableCell>{t('amount')}</TableCell>
              <TableCell />
              <TableCell>{t('period')}</TableCell>
              <TableCell colSpan={modify ? 3 : 1} />
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {!payments && <ListSectionSkeletonRow />}
          {payments && payments.map((payment, key) => {
            return <TableRow key={key}>
              <TableCell><ListSectionDatetime value={payment.paid_at} /></TableCell>
              <TableCell>
                {payment.amount_covered != null && formatMoney(payment.amount_covered) + ' / '}
                {formatMoney(payment.amount)}
                {Boolean(payment.amount_refunded) && <> (<span style={{ color: '#ff9800' }}>{formatMoney(payment.amount_refunded)}</span>)</>}
              </TableCell>
              <TableCell style={{ paddingTop: 4, paddingBottom: 4 }}>
                {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyAutomatic && (
                  <MobillyPaymentOptionChip
                    letter="A"
                    label="Automatic"
                  />
                )}
                {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyStartStop && (
                  <MobillyPaymentOptionChip
                    label="Start-stop"
                  />
                )}
                {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyMPay && (
                  <MobillyPaymentOptionChip
                    label="Mpay"
                  />
                )}
                {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyPostpay && (
                  <MobillyPaymentOptionChip
                    label="Postpay"
                  />
                )}
                {payment.option == ParkingLotSessionPaymentsPaymentOption.CashRegister && (
                  <PaymentOptionChip
                    label={t('cashRegister')}
                  />
                )}
                {payment.option == ParkingLotSessionPaymentsPaymentOption.CashDesk && (
                  <PaymentOptionChip
                    icon={<Money style={{ color: 'rgba(0, 0, 0, 0.38)' }} />}
                    label={t('cashDesk')}
                  />
                )}
              </TableCell>
              <TableCell>
                {(!payment.entry_at || !payment.exit_at) ? (<>{`—`}</>) : (<span style={{
                  ...((new Date(payment.exit_at).getTime() <= new Date(session.entry_at).getTime() || (session.exit_at && new Date(payment.entry_at).getTime() >= new Date(session.exit_at).getTime())) && { color: theme.palette.primary.main }),
                }}><ListSectionDatetime value={payment.entry_at} /> - <ListSectionDatetime value={payment.exit_at} /></span>)}
              </TableCell>
              <ListSectionIdCell id={payment.id} />
              {modify && (
                <>
                  <TableCell style={{ paddingLeft: 0, paddingRight: 16, width: 46 }}>
                    {payment.modify.refundable && (
                      <IconButton
                        color="secondary"
                        size="small"
                        style={{ margin: '-5px 0' }}
                        onClick={() => handleRefund(payment)}
                      >
                        <IconRefund />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell style={{ paddingLeft: 0, paddingRight: 16, width: 46 }}>
                    {payment.modify.detachable && (
                      <IconButton
                        color="secondary"
                        size="small"
                        style={{ margin: '-5px 0' }}
                        onClick={() => handleDetach(payment)}
                      >
                        <IconDetach />
                      </IconButton>
                    )}
                  </TableCell>
                </>
              )}
            </TableRow>
          })}
        </TableBody>
      </ListSectionBody>
      {modify && (
        <>
          <RefundPaymentDialog open={openRefundPaymentDialog} setOpen={setOpenRefundPaymentDialog} data={refundPaymentDialogData} />
        </>
      )}
    </>
  )
}

export default ListSessionPayments
